import { useState, useEffect } from 'react';
import { graphql, fetchQuery, useRelayEnvironment } from 'react-relay';

import { getBuyerId } from 'dibs-cookie-jar';

import { type useHasUnreadNotificationsQuery } from './__generated__/useHasUnreadNotificationsQuery.graphql.ts';

const query = graphql`
    query useHasUnreadNotificationsQuery($userId: String!) {
        viewer {
            user(userId: $userId) {
                conversationSummary {
                    unread
                    unreadActiveOffers
                }
            }
            actionRequiredOrders: transactions(
                viewName: "buyer.bomtActionRequired"
                buyerId: [$userId]
            ) {
                totalResults
                edges {
                    node {
                        serviceId
                    }
                }
            }
        }
    }
`;

export const useHasUnreadNotifications = ({
    hasRootPendingRefetch,
}: {
    hasRootPendingRefetch: boolean;
}): {
    hasUnreadNotifications: boolean;
    unreadOrdersNotificationsOrderIds: string[];
} => {
    const environment = useRelayEnvironment();

    const [userId, setUserId] = useState('');
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    const [unreadOrdersNotificationsOrderIds, setUnreadOrdersNotificationsOrderIds] = useState<
        string[]
    >([]);

    useEffect(() => {
        if (typeof document !== 'undefined') {
            setUserId(getBuyerId(document.cookie));
        }
    }, []);

    useEffect(() => {
        if (hasRootPendingRefetch || !userId) {
            return;
        }

        fetchQuery<useHasUnreadNotificationsQuery>(environment, query, { userId })
            .toPromise()
            .then(data => {
                const { user, actionRequiredOrders } = data?.viewer || {};
                const { conversationSummary } = user || {};
                const totalUnread = conversationSummary?.unread || 0;
                const unreadOffers = conversationSummary?.unreadActiveOffers || 0;
                const actionRequiredOrdersCount = actionRequiredOrders?.totalResults || 0;
                const unreadCount = totalUnread + unreadOffers + actionRequiredOrdersCount;
                const actionRequiredOrdersIds = (actionRequiredOrders?.edges || []).map(
                    edge => edge?.node?.serviceId || ''
                );
                setHasUnreadNotifications(!!unreadCount);
                setUnreadOrdersNotificationsOrderIds(actionRequiredOrdersIds);
            });
    }, [userId, environment, hasRootPendingRefetch]);

    return {
        hasUnreadNotifications,
        unreadOrdersNotificationsOrderIds,
    };
};
